import axios from 'axios';
import _ from 'lodash';
import { getDropoffsQuery, getDriversQuery, getProcessorsQuery, getRouteSessionsQuery, getRoutesQuery, getTrailersQuery, bulkQuery, getPickupsQuery } from './queries';
import { renameKey, arrayToMap, getNumSlips, resolveId } from '../../utils';
import { buildExpressQuery, buildLoopbackQuery } from './queries/builders';
import { calculateShrinkAmount, calculateShrinkPercentage } from '../helpers/dropoffs/utils';
import { getBulkQueryData } from './utils';
import Split from '../split';

const getHaulingDropoffDetails = (id, start, end, filterType = 'milk') =>
    new Promise((resolve, reject) => {
        const authQueryBuilder = Split.getBooleanTreatment('use_express_auth_endpoints') ? buildExpressQuery : buildLoopbackQuery;
        const migrationQueryBuilder = Split.getBooleanTreatment('use_express_migration_endpoints') ? buildExpressQuery : buildLoopbackQuery;
        const driverQuery = getDriversQuery({ fields: ['id', 'name'], haulingId: id, queryBuilder: authQueryBuilder });
        const driverRequest = axios.get(driverQuery);

        driverRequest.then((response) => {
            const drivers = Array.isArray(response.data) ? response.data.filter(Boolean).map(resolveId) : [];
            const driverIds = _.uniq(_.map(drivers, 'id'));

            const dropoffsQuery = bulkQuery(
                getDropoffsQuery,
                {
                    start,
                    end,
                    driverIds,
                    filterDeleted: true,
                    filterType,
                },
                'driverIds'
            );
            const dropoffsRequest = axios.post(dropoffsQuery.url, dropoffsQuery.body);

            return axios
                .all([dropoffsRequest])
                .then(
                    axios.spread((dropoffsResponse) => {
                        const dropoffs = dropoffsResponse?.data || [];
                        renameKey(dropoffs, '_id', 'id');

                        const processorIds = _.uniq(_.map(dropoffs, 'processor_id'));
                        const routeSessionIds = _.uniq(_.map(dropoffs, 'route_session_id'));

                        const processorQuery = getProcessorsQuery({ fields: ['id', 'name', 'geofence', 'latitude', 'longitude', 'license_number'], ids: processorIds, queryBuilder: authQueryBuilder });
                        const routeSessionQuery = bulkQuery(
                            getRouteSessionsQuery,
                            {
                                fields: ['id', 'BOL', 'status', 'route_id', 'route_number', 'trailer_id', 'truck_id', 'po_number'],
                                ids: routeSessionIds,
                                filterDeleted: true,
                                hint: '_id_1_created_at_-1_deleted_at_1',
                            },
                            'ids'
                        );

                        const processorRequest = axios.get(processorQuery);
                        const routeSessionRequest = axios.post(routeSessionQuery.url, routeSessionQuery.body);

                        return axios.all([processorRequest, routeSessionRequest]).then(
                            axios.spread((...responses) => {
                                const processors = responses?.[0]?.data || [];
                                const routeSessions = responses?.[1]?.data || [];
                                renameKey(processors, '_id', 'id');
                                renameKey(routeSessions, '_id', 'id');

                                const routeIds = _.uniq(_.map(routeSessions, 'route_id'));
                                const routeQuery = bulkQuery(getRoutesQuery, { fields: ['id', 'name'], ids: routeIds });

                                const routeRequest = axios.post(routeQuery.url, routeQuery.body);

                                const trailerIds = _.uniq(_.map(routeSessions, 'trailer_id'));
                                const trailerRequest = getBulkQueryData(getTrailersQuery, { ids: trailerIds, queryBuilder: migrationQueryBuilder });

                                const pickupsQuery = bulkQuery(getPickupsQuery, { routeSessionIds, filterDeleted: true, populate: { producer_id: ['frozen'] } });
                                const pickupsRequest = axios.post(pickupsQuery.url, pickupsQuery.body);

                                return axios.all([routeRequest, trailerRequest, pickupsRequest]).then(
                                    axios.spread((...subResponses) => {
                                        const routes = subResponses?.[0]?.data || [];
                                        renameKey(routes, '_id', 'id');
                                        const trailers = subResponses?.[1] || [];
                                        const trailerMap = arrayToMap(trailers, 'id');
                                        const pickups = subResponses?.[2]?.data || [];

                                        const data = _.map(dropoffs, (value) => {
                                            const processor = [_.find(processors, { id: value.processor_id })];
                                            const driver = [_.find(drivers, { id: value.driver_id })];
                                            const routeSession = _.find(routeSessions, { id: value.route_session_id });
                                            const routeSessionPickups = pickups.filter((pickup) => pickup.route_session_id === routeSession.id);
                                            return {
                                                ...value,
                                                shrink_amount: calculateShrinkAmount(value),
                                                shrink_percent: calculateShrinkPercentage(value),
                                                processor,
                                                driver,
                                                route_session: [{ ...routeSession, route: routeSession && _.find(routes, { id: routeSession.route_id }) ? _.find(routes, { id: routeSession.route_id }).name : '' }],
                                                trailer_number: trailerMap?.[routeSession?.trailer_id]?.trailer_number || '',
                                                num_slips: getNumSlips(value),
                                                pickups: routeSessionPickups,
                                            };
                                        });

                                        resolve({ dropoffs: data });
                                    })
                                );
                            })
                        );
                    })
                )
                .catch((errors) => {
                    // eslint-disable-next-line no-console
                    console.log(errors);
                    reject(new Error('Unable to process request'));
                });
        });
    });

export default getHaulingDropoffDetails;
